import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDown, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { getFeatured, getSiteSettings, listCompetitions, listWinners, joinWaitlist } from '@/lib/api';
import { formatGBP, formatGBPfromPence, calculateOrderTotalPence, formatNumber, formatDateShort } from '@/lib/format';
import PadelCourtSVG from '@/components/brand/PadelCourtSVG';
import YellowRule from '@/components/brand/YellowRule';
import CountdownTimer from '@/components/comp/CountdownTimer';
import ProgressBar from '@/components/comp/ProgressBar';
import CompetitionCard from '@/components/comp/CompetitionCard';

const CATEGORIES = [
  { key: 'Padel Holidays', label: 'Padel Holidays', img: 'https://images.pexels.com/photos/24807133/pexels-photo-24807133.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { key: 'Premium Rackets', label: 'Premium Rackets', img: 'https://images.pexels.com/photos/37978261/pexels-photo-37978261.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { key: 'Club Memberships', label: 'Club Memberships', img: 'https://images.pexels.com/photos/38254515/pexels-photo-38254515.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
  { key: 'Bundles', label: 'Bundles', img: 'https://picsum.photos/seed/bundles/900/650' },
  { key: 'HYROX Packages', label: 'HYROX Packages', img: 'https://picsum.photos/seed/hyrox/900/650' },
];

const UPCOMING = [
  { name: 'Mystery Racket Drop', when: 'Dropping soon', img: 'https://picsum.photos/seed/coming1/900/650' },
  { name: 'City Break for Two', when: 'Dropping soon', img: 'https://picsum.photos/seed/coming2/900/650' },
  { name: 'Coaching Weekend', when: 'Dropping soon', img: 'https://picsum.photos/seed/coming3/900/650' },
];

// Brand-owned tiles used in the "Follow @thepadeldraw" grid.
const INSTAGRAM_TILES = [
  'https://customer-assets-v7afamib.emergentagent.net/job_yellow-line/artifacts/hopbcp24_IMG_1236.png',
  'https://customer-assets-v7afamib.emergentagent.net/job_yellow-line/artifacts/6jk7svob_IMG_1237.png',
  'https://customer-assets-v7afamib.emergentagent.net/job_yellow-line/artifacts/fywo2p41_IMG_1238.png',
  'https://customer-assets-v7afamib.emergentagent.net/job_yellow-line/artifacts/rilvd3o3_IMG_1239.png',
  'https://customer-assets-v7afamib.emergentagent.net/job_yellow-line/artifacts/w2ttkbz8_IMG_1241.png',
];

// Hoisted framer-motion configs — stable object identity across renders.
const HERO_PILL_INITIAL_REDUCED = { opacity: 1 };
const HERO_PILL_INITIAL = { opacity: 0, y: 12 };
const HERO_PILL_ANIMATE = { opacity: 1, y: 0 };
const HERO_PILL_TRANSITION = { duration: 0.35, delay: 0.15 };

const HERO_H1_INITIAL_REDUCED = { opacity: 1 };
const HERO_H1_INITIAL = { opacity: 0, y: 20 };
const HERO_H1_ANIMATE = { opacity: 1, y: 0 };
const HERO_H1_TRANSITION = { duration: 0.5, delay: 0.2 };

const HERO_SUB_INITIAL_REDUCED = { opacity: 1 };
const HERO_SUB_INITIAL = { opacity: 0 };
const HERO_SUB_ANIMATE = { opacity: 1 };
const HERO_SUB_TRANSITION = { duration: 0.4, delay: 0.5 };

const HomePage = () => {
  const reduce = useReducedMotion();
  const [featured, setFeatured] = useState(null);
  const [comps, setComps] = useState([]);
  const [winners, setWinners] = useState([]);
  const [waitEmail, setWaitEmail] = useState('');
  const [waitLoading, setWaitLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState({}); // { hero_title_override, hero_subtitle_override }
  // Track when both hero-copy inputs (siteSettings + featured) have settled
  // so we can hold the hero copy hidden for the first tick or two rather
  // than briefly flash a hardcoded fallback that then gets replaced. Set
  // to true when EITHER request has finished (success OR error) \u2014 the
  // second one will just refine what we already show.
  const [heroReady, setHeroReady] = useState(false);

  useEffect(() => {
    let settled = 0;
    const done = () => {
      settled += 1;
      if (settled >= 2) setHeroReady(true);
    };
    getFeatured().then(setFeatured).catch(() => {}).finally(done);
    listCompetitions('LIVE').then((d) => setComps(d.items || [])).catch(() => {});
    listWinners().then((d) => setWinners((d.items || []).slice(0, 6))).catch(() => {});
    getSiteSettings().then(setSiteSettings).catch(() => {}).finally(done);
  }, []);

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!waitEmail) return;
    setWaitLoading(true);
    try {
      await joinWaitlist(waitEmail, 'homepage_coming_soon');
      toast.success("You're on the list. Well be in touch.");
      setWaitEmail('');
    } catch (err) {
      toast.error('Could not join waitlist');
    } finally {
      setWaitLoading(false);
    }
  };

  // Memoise the "other live competitions" list so we don't re-filter/slice on
  // every keystroke of the waitlist email or countdown-tick re-render.
  const otherComps = useMemo(
    () => comps.filter((c) => c.slug !== featured?.slug).slice(0, 6),
    [comps, featured?.slug],
  );

  // Memoise the winners preview (max 6 for the homepage grid) so the array
  // identity is stable across unrelated re-renders.
  const winnersPreview = useMemo(() => winners.slice(0, 6), [winners]);

  // Resolve the hero copy in priority order:
  //   1. Admin override (from site_settings) — wins if non-empty
  //   2. Derived from featured competition — "Win [title]" + subtitle
  //   3. Static fallback for the moment the API hasn't loaded yet
  const heroTitleRaw = useMemo(() => {
    const override = (siteSettings?.hero_title_override || '').trim();
    if (override) return override;
    if (featured?.title) return `Win ${featured.title}`;
    return 'Win a brand-new padel prize';
  }, [siteSettings, featured]);

  const heroSubtitleRaw = useMemo(() => {
    const override = (siteSettings?.hero_subtitle_override || '').trim();
    if (override) return override;
    // Source of truth is the featured comp's prize_description so a single
    // admin edit propagates to hero + card + comp-page without drift.
    if (featured?.prize_description) return featured.prize_description;
    if (featured?.subtitle) return featured.subtitle;
    return 'Enter now for your shot at the prize.';
  }, [siteSettings, featured]);

  const heroPriceText = useMemo(() => {
    const p = Number(featured?.ticket_price_gbp);
    if (!Number.isFinite(p) || p <= 0) return '';
    return `From £${p.toFixed(2)}`;
  }, [featured]);

  const heroOddsText = useMemo(() => {
    const t = Number(featured?.total_tickets);
    if (!Number.isFinite(t) || t <= 0) return null;
    return `1 in ${t.toLocaleString('en-GB')} Odds`;
  }, [featured]);

  // Parse *italic* markers into <em class="font-italic-serif">…</em>. Wrap
  // everything else in <span>. Split on * and alternate — odd indices are italic.
  const renderInline = (raw) => {
    if (!raw) return null;
    const parts = String(raw).split('*');
    return parts.map((p, i) => (
      i % 2 === 1
        ? <em key={i} className="font-italic-serif">{p}</em>
        : <span key={i}>{p}</span>
    ));
  };

  return (
    <>
      {/* HERO ---------------------------------------------------------------
       *
       * Background photograph strategy (per the "Field & Fortune" reference):
       *   - Real, atmospheric outdoor padel-court photo (Pexels 36014317
       *     "Two men enjoying padel outdoors surrounded by trees", by Ashford
       *     Marx — free commercial license, no attribution required per the
       *     Pexels License).
       *   - Sits underneath a two-layer overlay: a heavy dark racing-green
       *     wash (opacity ~78–82%) and a directional left→right gradient
       *     that pushes the darkest values behind the headline text on the
       *     left, letting the imagery slowly emerge on the right.
       *   - The photo therefore reads as *atmosphere*, not foreground.
       *   - We preload the image (fetchpriority=high) so it doesn't pop in.
       * ------------------------------------------------------------------- */}
      <section className="relative overflow-hidden isolate" data-testid="home-hero">
        {/* Background photograph — AI-generated on-brand asset (Gemini
            nano-banana, prompted from a real UK Game4Padel reference
            shot, with people/signage/branding stripped out). Reads as
            an idealised country-club lawn rather than a specific venue.
            Stored locally as /public/hero_padel.webp so it's served
            from our own domain (no external CDN, no rate limits). */}
        <img
          src="/hero_padel.webp"
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchpriority="high"
          className="absolute inset-0 -z-20 w-full h-full object-cover object-center"
          data-testid="hero-background-photo"
        />
        {/* Dark racing-green wash — the primary "fade" that keeps text
            legible against a bright-sky photograph. Tuned stronger than
            the previous beach image: left-anchor near-opaque (0.94) for
            the headline column, and a heavier top vignette (0.55) to
            tame the blue sky so the cream copy reads cleanly. */}
        <div
          className="absolute inset-0 -z-10 pointer-events-none"
          style={{
            background: [
              'linear-gradient(90deg, rgba(20,37,28,0.94) 0%, rgba(20,37,28,0.84) 50%, rgba(20,37,28,0.55) 100%)',
              'linear-gradient(180deg, rgba(20,37,28,0.55) 0%, rgba(20,37,28,0.30) 45%, rgba(20,37,28,0.80) 100%)',
            ].join(', '),
          }}
          data-testid="hero-overlay"
        />
        <div className="noise-overlay" />
        <div className="container-pd relative pt-8 md:pt-20 pb-6 md:pb-20">
          <motion.div
            initial={reduce ? HERO_PILL_INITIAL_REDUCED : HERO_PILL_INITIAL}
            animate={HERO_PILL_ANIMATE}
            transition={HERO_PILL_TRANSITION}
            className="inline-flex items-center gap-2 border border-[color:var(--pd-text)]/25 bg-[color:var(--pd-surface)]/40 rounded-full px-3 py-1.5 mb-3 md:mb-5"
          >
            <span className="h-2 w-2 rounded-full bg-[color:var(--pd-text)] animate-pulse" />
            <span className="font-label text-[11px] text-[color:var(--pd-text)]">Competition open</span>
          </motion.div>
          <motion.h1
            initial={reduce ? HERO_H1_INITIAL_REDUCED : HERO_H1_INITIAL}
            animate={HERO_H1_ANIMATE}
            transition={HERO_H1_TRANSITION}
            className="font-display text-[2.75rem] sm:text-6xl md:text-[5.75rem] leading-[0.95] tracking-[-0.01em] max-w-4xl min-h-[1.2em]"
            data-testid="hero-headline"
            style={{ visibility: heroReady ? undefined : 'hidden' }}
            aria-hidden={!heroReady}
          >
            {renderInline(heroTitleRaw)}
          </motion.h1>
          <motion.p
            initial={reduce ? HERO_SUB_INITIAL_REDUCED : HERO_SUB_INITIAL}
            animate={HERO_SUB_ANIMATE}
            transition={HERO_SUB_TRANSITION}
            className="mt-3 md:mt-5 text-base md:text-lg text-white/85 max-w-2xl min-h-[1.5em]"
            data-testid="hero-subtitle"
            style={{ visibility: heroReady ? undefined : 'hidden' }}
            aria-hidden={!heroReady}
          >
            {renderInline(heroSubtitleRaw)}
          </motion.p>

          {/* THREE compliance + trust badges — replaces the earlier four
              checkmark pills. The free-postal-entry badge is doing real
              legal/trust work (UK prize-competition law requires a visible
              free-entry route) — it is NOT decorative. Odds pull from the
              featured competition's total_tickets so the number stays
              correct even after admin swaps the comp. */}
          <div className="mt-4 md:mt-5 flex flex-wrap gap-1.5 md:gap-2" data-testid="hero-trust-badges">
            {heroOddsText ? (
              <span className="hero-badge" data-testid="hero-badge-odds">
                <span className="hero-badge-icon" aria-hidden="true">🎯</span>
                {heroOddsText}
              </span>
            ) : null}
            <span className="hero-badge" data-testid="hero-badge-free-entry">
              <span className="hero-badge-icon" aria-hidden="true">✓</span>
              Free Postal Entry Available
            </span>
            <span className="hero-badge" data-testid="hero-badge-guaranteed-draw">
              <span className="hero-badge-icon" aria-hidden="true">🛡️</span>
              Guaranteed Draw Date
            </span>
          </div>

          <div className="mt-5 md:mt-7 flex flex-col items-start gap-2">
            <Link
              to={featured?.slug ? `/competitions/${featured.slug}` : '/competitions'}
              className="bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-6 py-3.5 rounded-xl font-medium hover:brightness-95 inline-flex items-center gap-2 text-base md:text-lg"
              data-testid="hero-enter-now-button"
            >
              {featured?.status === 'COMING_SOON'
                ? 'Preview next draw'
                : `Enter Draw${heroPriceText ? ` \u2014 ${heroPriceText}` : ''}`}
            </Link>
            <Link
              to="/how-it-works"
              className="text-[color:var(--pd-accent-yellow)] text-sm underline underline-offset-4 hover:brightness-110 inline-flex items-center gap-1"
              data-testid="hero-how-it-works-link"
            >
              How draws work &rsaquo;
            </Link>
          </div>
        </div>
      </section>

      <YellowRule />

      {/* LIVE COMPETITION STRIP */}
      {featured && (
        <section className="section" data-testid="home-live-strip">
          <div className="container-pd">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 bg-surface border border-pd rounded-2xl overflow-hidden">
              <div className="md:col-span-7 relative aspect-[16/10] md:aspect-auto md:min-h-[440px]">
                <img src={featured.hero_image_url} alt={featured.title} className="absolute inset-0 w-full h-full object-cover heritage-image" />
                <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--pd-bg)] via-transparent to-transparent md:from-transparent md:via-transparent md:to-[color:var(--pd-surface)]" />
                {featured.status === 'COMING_SOON' ? (
                  <span className="absolute top-4 left-4 bg-[color:var(--pd-gold)] text-[#14251c] font-mono text-[10px] tracking-widest uppercase px-2 py-1 rounded" data-testid="live-strip-badge">Coming soon</span>
                ) : (
                  <span className="absolute top-4 left-4 bg-[color:var(--pd-accent-yellow)] text-[#14251c] font-mono text-[10px] tracking-widest uppercase px-2 py-1 rounded" data-testid="live-strip-badge">Live now</span>
                )}
              </div>
              <div className="md:col-span-5 p-5 md:p-7 flex flex-col justify-between">
                <div>
                  <div className="font-mono text-[10px] text-muted-sage uppercase tracking-[0.16em]">{featured.category}</div>
                  <h2 className="font-display text-4xl md:text-5xl leading-[0.95] mt-2">{featured.title}</h2>
                  <p className="text-sm text-muted-sage mt-3">{featured.subtitle}</p>
                  <div className="mt-5 flex items-baseline gap-3">
                    <div className="font-display text-4xl text-[color:var(--pd-accent-yellow)]">{formatGBP(featured.ticket_price_gbp)}</div>
                    <div className="font-mono text-xs text-muted-sage">/ 1st ticket</div>
                  </div>
                  <div className="mt-1 font-mono text-xs text-[color:var(--pd-accent-yellow)]" data-testid="hero-pair-price">2 for {formatGBPfromPence(calculateOrderTotalPence(featured.ticket_price_gbp, 2))}</div>
                  {featured.status === 'COMING_SOON' ? (
                    // Coming-soon teaser copy — no tickets sold yet, no draw
                    // countdown noise, just the promise of what's coming.
                    <div className="mt-3 guarantee-box" data-testid="live-strip-guarantee">
                      Currently in preparation. {featured.go_live_at
                        ? <>Opens for entries on <strong>{formatDateShort(featured.go_live_at)}</strong>.</>
                        : <>Ticket sales and the free postal entry route will open shortly.</>}
                    </div>
                  ) : (
                    <div className="mt-3 guarantee-box" data-testid="live-strip-guarantee">
                      Draw goes ahead on <strong>{formatDateShort(featured.draw_date)}</strong>, no matter how many tickets sell. Lower sales mean better odds for you.
                    </div>
                  )}
                </div>
                {/* Progress bar hidden while COMING_SOON — showing "0 of N sold"
                    on a preview looks like a broken/empty live competition. */}
                {featured.status !== 'COMING_SOON' ? (
                  <div className="mt-5">
                    <ProgressBar sold={featured.tickets_sold} total={featured.total_tickets} />
                    <div className="mt-2 font-mono text-xs text-muted-sage">
                      {formatNumber(featured.tickets_remaining)} tickets remaining
                    </div>
                  </div>
                ) : null}
                {/* Countdown target: go_live_at while COMING_SOON (users care
                    when they can enter), draw_date while LIVE. */}
                <div className="mt-5">
                  <div className="font-mono text-[10px] text-muted-sage uppercase mb-2">
                    {featured.status === 'COMING_SOON' && featured.go_live_at
                      ? <>Opens {formatDateShort(featured.go_live_at)}</>
                      : <>Draw date {formatDateShort(featured.draw_date)}</>}
                  </div>
                  <CountdownTimer
                    isoDate={featured.status === 'COMING_SOON' && featured.go_live_at ? featured.go_live_at : featured.draw_date}
                    dataTestidPrefix="home-countdown"
                  />
                </div>
                <Link
                  to={`/competitions/${featured.slug}${featured.status === 'COMING_SOON' ? '' : '?qty=1'}`}
                  className={featured.status === 'COMING_SOON'
                    ? "mt-5 bg-[color:var(--pd-gold)]/25 text-[color:var(--pd-gold)] border-2 border-[color:var(--pd-gold)]/40 px-4 py-3 rounded-xl font-medium text-center"
                    : "mt-5 bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 py-3 rounded-xl font-medium text-center"
                  }
                  data-testid="live-strip-cta-button"
                >
                  {featured.status === 'COMING_SOON'
                    ? <>Preview &rsaquo;</>
                    : <>1 Ticket &mdash; {formatGBP(featured.ticket_price_gbp)}</>}
                </Link>
                {featured.status !== 'COMING_SOON' ? (
                  <Link
                    to={`/competitions/${featured.slug}?qty=2`}
                    className="mt-2 border-2 border-[color:var(--pd-accent-yellow)]/70 text-[color:var(--pd-accent-yellow)] px-4 py-3 rounded-xl font-medium text-center hover:bg-[color:var(--pd-accent-yellow)]/10"
                    data-testid="live-strip-qty2-button"
                  >
                    2 Tickets &mdash; {formatGBPfromPence(calculateOrderTotalPence(featured.ticket_price_gbp, 2))}
                  </Link>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS — cream panel for breathing room against the dark hero
          and the dark live-comp card. Also the single on-homepage place where
          compliance copy (free-postal-entry route, seeded + witnessed RNG)
          is explained in real sentences, per the UX review.
          Wrapped in a <div> so we can bracket the cream with soft gradient
          "transition bands" above and below. The racing-green → cream jump
          is a 60+ L* contrast swing; without a fade it reads as two glued
          posters. The band blends through a dusty sage mid-tone so the
          cream emerges like morning mist off a club lawn — on-brand for
          the UK country-club aesthetic — and the thin gold hairline in
          the middle gives it a gilt-edge, book-spine finish. */}
      <div className="relative" data-testid="home-how-it-works-wrap">
        {/* Top fade: racing-green → dusty sage → cream */}
        <div
          aria-hidden="true"
          className="h-12 md:h-20 w-full relative"
          style={{
            background: 'linear-gradient(to bottom, var(--pd-bg) 0%, #2a3f33 35%, #a8aa9c 75%, var(--pd-cream) 100%)',
          }}
        >
          {/* Gilt hairline — sits ~ 60% of the way down the fade,
              echoes the brass-gold brand accent, visually ties the two
              zones together. */}
          <div className="absolute left-1/2 -translate-x-1/2 top-[62%] h-px w-20 md:w-28 bg-[color:var(--pd-gold)]/55" />
        </div>

        <section className="bg-[color:var(--pd-cream)] text-[color:var(--pd-bg)] py-14 md:py-24" data-testid="home-how-it-works">
        <div className="container-pd">
          <div className="max-w-2xl mb-8 md:mb-12">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[color:var(--pd-gold)]">How it works</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mt-3">Three steps. One winner. Zero fine print.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {[
              { n: '01', t: 'Choose your tickets', d: 'Pick the prize you want and the number of tickets to buy. Every ticket is one entry.' },
              { n: '02', t: 'Answer the question', d: 'A simple skill question keeps every competition on the right side of UK prize-competition law.' },
              { n: '03', t: 'Win the prize', d: 'Draw happens live with a verified random seed. We publish everything so anyone can double-check the result.' },
            ].map((s) => (
              <div key={s.n} className="bg-white/60 border border-[color:var(--pd-bg)]/15 rounded-2xl p-5 md:p-6">
                <div className="font-display text-5xl md:text-6xl text-[color:var(--pd-gold)] leading-none">{s.n}</div>
                <div className="font-display text-xl md:text-2xl mt-2 md:mt-3">{s.t}</div>
                <p className="text-sm text-[color:var(--pd-bg)]/70 mt-2 md:mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 md:mt-8 text-sm text-[color:var(--pd-bg)]/70 max-w-3xl">
            <strong className="text-[color:var(--pd-bg)]">No purchase necessary.</strong> Every competition offers a genuine free postal-entry route — the address and full instructions are on the competition page. Postal entries sit in the exact same draw pool as paid tickets, with the exact same odds.
          </p>
        </div>
      </section>

        {/* Bottom fade: cream → dusty sage → racing-green (mirror of top) */}
        <div
          aria-hidden="true"
          className="h-12 md:h-20 w-full relative"
          style={{
            background: 'linear-gradient(to bottom, var(--pd-cream) 0%, #a8aa9c 25%, #2a3f33 65%, var(--pd-bg) 100%)',
          }}
        >
          <div className="absolute left-1/2 -translate-x-1/2 top-[38%] h-px w-20 md:w-28 bg-[color:var(--pd-gold)]/55" />
        </div>
      </div>

      {/* PRIZE CATEGORIES — hidden by default while only one competition is
          active (admin can re-enable via Admin → Homepage toggle). */}
      {siteSettings?.show_prize_categories ? (
      <section className="section pt-0">
        <div className="container-pd">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-3xl md:text-4xl">Prize categories</h2>
            <Link to="/competitions" className="font-mono text-xs text-muted-sage hover:text-[color:var(--pd-accent-yellow)]">Browse all →</Link>
          </div>
          <div className="flex md:grid md:grid-cols-5 gap-4 overflow-x-auto md:overflow-visible pb-2" data-testid="home-categories">
            {CATEGORIES.map((c) => (
              <Link key={c.key} to={`/competitions?category=${encodeURIComponent(c.key)}`} className="group min-w-[260px] md:min-w-0 bg-surface border border-pd rounded-2xl overflow-hidden hover:border-[color:var(--pd-accent-yellow)]/50">
                <div className="aspect-[4/3] relative">
                  <img src={c.img} alt={c.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--pd-bg)] via-transparent to-transparent" />
                </div>
                <div className="p-4">
                  <div className="font-display text-lg">{c.label}</div>
                  <div className="font-mono text-xs text-muted-sage mt-1 group-hover:text-[color:var(--pd-accent-yellow)]">Browse prizes →</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      ) : null}

      {/* WINNERS — removed entirely per UX review (previously held a
          "Launch mode / first winner on the way" empty-state card that
          read as low-trust while we have zero winners). The real section
          will be re-introduced after our first draw completes. */}

      {/* COMING SOON — hidden by default while only one competition is
          active (admin can re-enable via Admin → Homepage toggle). */}
      {siteSettings?.show_coming_soon_grid ? (
      <section className="section pt-0">
        <div className="container-pd">
          <div className="flex items-end justify-between mb-6 md:mb-8">
            <h2 className="font-display text-3xl md:text-5xl leading-none">Coming soon</h2>
            <div className="h-[2px] w-24 bg-[color:var(--pd-accent-yellow)] hidden md:block" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {UPCOMING.map((u) => (
              <div key={u.name} className="relative bg-surface border border-pd rounded-2xl overflow-hidden">
                <div className="aspect-[16/10] relative">
                  <img src={u.img} alt="Coming soon" className="absolute inset-0 w-full h-full object-cover blur-md scale-105" />
                  <div className="absolute inset-0 bg-[color:var(--pd-bg)]/60 flex items-center justify-center">
                    <span className="font-display text-7xl text-[color:var(--pd-accent-yellow)]">?</span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="font-display text-xl">{u.name}</div>
                  <div className="font-mono text-xs text-muted-sage mt-1">{u.when}</div>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={submitWaitlist} className="mt-8 bg-surface border border-pd rounded-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="home-waitlist-form">
            <div>
              <div className="font-display text-2xl">Be first to know</div>
              <div className="text-sm text-muted-sage">Drop your email. We'll send one message a week. No fluff.</div>
            </div>
            <div className="flex-1 flex gap-2">
              <input
                type="email"
                required
                value={waitEmail}
                onChange={(e) => setWaitEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 h-12 rounded-xl bg-[color:var(--pd-bg)] border border-pd px-4 text-sm focus:border-[color:var(--pd-accent-yellow)]"
                data-testid="waitlist-email-input"
              />
              <button
                type="submit"
                disabled={waitLoading}
                className="bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-5 rounded-xl font-medium disabled:opacity-60"
                data-testid="waitlist-submit-button"
              >
                {waitLoading ? 'Adding…' : 'Notify me'}
              </button>
            </div>
          </form>
        </div>
      </section>
      ) : null}

      {/* OTHER LIVE COMPETITIONS */}
      {comps.length > 1 && (
        <section className="section pt-0">
          <div className="container-pd">
            <YellowRule className="mb-6 md:mb-10" />
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-display text-3xl md:text-4xl">Other live competitions</h2>
              <Link to="/competitions" className="font-mono text-xs text-muted-sage hover:text-[color:var(--pd-accent-yellow)]">All competitions →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherComps.map((c) => (
                <CompetitionCard key={c.id} comp={c} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* INSTAGRAM PLACEHOLDER */}
      <section className="section pt-0">
        <div className="container-pd">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-3xl md:text-4xl">Follow @thepadeldraw</h2>
            <a href="https://instagram.com/thepadeldraw" target="_blank" rel="noreferrer" className="font-mono text-xs text-muted-sage hover:text-[color:var(--pd-accent-yellow)]">Visit Instagram →</a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="instagram-grid">
            {INSTAGRAM_TILES.map((src, i) => (
              <div key={src} className="aspect-square bg-surface border border-pd overflow-hidden rounded-lg">
                <img src={src} alt="@thepadeldraw" className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
